import {
  CustomSelect,
  TextInput,
  Uploader,
  SelectedMediaCard,
  Button,
  toast,
  TextArea,
} from "@/components";
import { useEffect, useRef, useState, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  callRegisterCustomMediaOnCms,
  callRegisterMediaOnCms,
} from "@/store/upload/upload.action";
import { connect, ConnectedProps } from "react-redux";
import { findURLType, extractUrlFromEmbed } from "./utils";
import { stripExtension } from "@/utils/ui/uiFunctions";
import { RootState, store } from "@/store/index";
import { showUploadProgress } from "@/store/app/app.slice";
import Modal from "../Modal/Modal";
import {
  getAdminCurrentPresetDetails,
  getCurrentPresetDetails,
  getMedia,
} from "@/store/media/media.action";
import { Ellipsis } from "lucide-react";
import {
  addFile,
  setUploadedSuccessfully,
  updateFileProgress,
} from "@/store/upload/upload.slice";
import {
  emptyMediaDetails,
  setMediaDetails,
  emptyAssetDetails,
  updateMediaStatus,
} from "@/store/media/media.slice";
import { MediaFeatureMapKey } from "@/utils/utilFunctions/Mapper/CustomUploadConfig";
import { CustomUploadConfig } from "@/utils/utilFunctions/Mapper/CustomUploadConfig";

import { FTPAddForm } from "./CustomUploads/FTPForm";
import { getPresetList } from "@/store/media/media.action";
import {
  getMediaDimensions,
  computeAspectRatioAndOrientation,
} from "@/utils/createOptionsUtils";
import SocialPublish from "@/components/MediaPublish/SocialPublish/SocialPublish";
import CreateMetaPreset from "@/components/MetaPresets/CreateMetaPreset";
import ProcessingPreset from "@/components/MetaPresets/ProcessingPreset";
import BrandingPreset from "@/components/MetaPresets/BrandingPreset";

import { getListProduct } from "@/store/constants/constants.action";
import { useUrlValidation } from "./hooks/useUrlValidation";
import { usePresetManagement } from "./hooks/usePresetManagement";
import { getAdminPresetList } from "@/store/media/media.action";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";

export interface CustomVideoForm {
  url: string;
  meta?: string;
  publishing?: string;
  processing?: string;
  branding?: string;
  title?: string;
  desc?: string;
  // Optional fields for features that may be enabled via mediaFeatures
  video_flavour?: string;
  audio_flavour?: string;
  encryption?: boolean;
  generate_video_preview?: boolean;
  generate_ai_thumbnail?: boolean;
  generate_embedded_podcast?: boolean;
  generate_transcript?: boolean;
  audio_waveform?: boolean;
  language?: string;
  isPreset?: boolean;
}

interface IVideo {
  closeModal: () => void;
  openUpload?: any;
  uploadType: string;
  isChangingTab: boolean;
  onValidatingUrlChange?: (isValidating: boolean) => void;
  onFormChange?: (hasChanges: boolean) => void;
  isPreset?: boolean;
  onNextClick?: () => void;
  isNextDisabled?: boolean;
  nextButtonText?: string;
  isWizard?: boolean;
}

const Video = ({
  closeModal,
  callRegisterMediaOnCms,
  callRegisterCustomMediaOnCms,
  showUploadProgress,
  updateFileProgress,
  addFile,
  openUpload,
  setUploadedSuccessfully,
  emptyMediaDetails,
  emptyAssetDetails,
  uploadType,
  activeCustomUploadTab,
  isChangingTab,
  onValidatingUrlChange,
  onFormChange,
  presetList,
  getPresetList,
  getMedia,
  singleCurrentUser,
  productList,
  getListProduct,
  onNextClick,
  getCurrentPresetDetails,
  currentPresetDetails,
  bunchProductList,
  getAdminPresetList,
  adminPresetList,
  adminCurrentPresetDetails,
  getAdminCurrentPresetDetails,
  isWizard = false,
}: Props) => {
  const uploadingRef = useRef(false);
  const uploadingRefId = useRef("");
  const UploadCallback = useRef<any>(null);
  const [showFTPForm, setShowFTPForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [isNextButtonClicked, setIsNextButtonClicked] = useState(false);
  const nextButtonClickedRef = useRef(false);
  const disableBlurValidationRef = useRef(false);
  const mediaFeatures = CustomUploadConfig(
    activeCustomUploadTab as MediaFeatureMapKey
  );
  const meta_config: Record<string, any> = {};

  // Ref to store fileType for use in validation schema
  const fileTypeRef = useRef<string | null>(null);

  const featureMappings = {
    video_quality: "video_flavour",
    audio_quality: "audio_flavour",
    encryption: "encryption",
    video_preview: "generate_video_preview",
    ai_thumbnail: "generate_ai_thumbnail",
    embedded_podcast: "generate_embedded_podcast",
    transcript: "generate_transcript",
    audio_waveform: "audio_waveform",
    language: "language",
  };

  const getEncodedUrl = async (url: string) => {
    formik.setFieldError("url", "");
    formik.setFieldTouched("url", true);
    formik.setFieldValue("url", url);
    formik.validateField("url");
    handleUrlValidation(url);
    setShowFTPForm(false);
  };

  // Initialize formik with basic validation schema
  const formik = useFormik<CustomVideoForm>({
    initialValues: {
      url: "",
      meta: "",
      publishing: "",
      processing: "",
      branding: "",
      title: "",
      desc: "",
    } as CustomVideoForm,
    validateOnMount: false,
    validationSchema: () =>
      Yup.object({
        url: Yup.string().required("URL is required"),
        title: Yup.string().required("Title is required"),
      }),
    onSubmit: (values) => {
      const normalizedUrl = extractUrlFromEmbed(values.url);
      values = { ...values, url: normalizedUrl };
      const type = findURLType(normalizedUrl);

      showUploadProgress();

      // Build meta_config from enabled features (only if fields exist in formik values)
      for (const [key, valueKey] of Object.entries(featureMappings)) {
        if (
          mediaFeatures[key as keyof typeof mediaFeatures]?.enabled &&
          values[valueKey as keyof typeof values] !== undefined
        ) {
          meta_config[key] = values[valueKey as keyof typeof values];
        }
      }

      const data = {
        ...values,
        content: uploadType === "audio" ? "audio" : "video",
        meta_config: meta_config,
        preset_config: {
          meta: formik.values.meta,
          social: formik.values.publishing,
          processing: formik.values.processing,
        },
      };

      if (selectedFile) {
        selectedFile.data = data;
        if (type === "gdrive" && urlValidationRefs.current.gdriveMD5Checksum) {
          selectedFile.fid = urlValidationRefs.current.gdriveMD5Checksum;
        }

        uploadingRef.current = true;
        callRegisterMediaOnCms(selectedFile, (response: any) => {
          if (response && response.status === 200) {
            if (response?.data?.preset_config?.meta) {
              localStorage.setItem(
                "metaPresetId",
                response.data.preset_config.meta
              );
            } else {
              const metaId = productList.find(
                (item: any) => item?.id === singleCurrentUser?.team
              )?.presets?.meta;
              localStorage.setItem("metaPresetId", metaId);
            }
            if (response?.data?.preset_config?.social) {
              localStorage.setItem(
                "socialPresetId",
                response.data.preset_config.social
              );
            } else {
              const socialId = productList.find(
                (item: any) => item?.id === singleCurrentUser?.team
              )?.presets?.social;
              localStorage.setItem("socialPresetId", socialId);
            }
            setTimeout(() => {
              getMedia({ id: response.data.id });
            }, 200);

            uploadingRefId.current = response?.data?.id;
            const id = response.data.id;
            const token = response.data.jwt;
            const query = {
              entity: id,
              key: "media",
              method: "asset.file",
            };
            UploadCallback.current && UploadCallback.current(query, token);
            openUpload();
            closeModal();
          } else {
            toast.fail(response.error);
            closeModal();
          }
        });
      } else {
        showUploadProgress();
        emptyMediaDetails();
        emptyAssetDetails();
        callRegisterCustomMediaOnCms(
          {
            type,
            data,
            name: "media",
            title: formik.values.title || urlValidationRefs.current.fileName,
            descriptions:
              formik.values.desc ||
              urlValidationRefs.current.youtubedescription,
            tags: urlValidationRefs.current.youtubetags,
            preset_config: {
              meta: formik.values.meta,
              social: formik.values.publishing,
              processing: formik.values.processing,
            },
            downloads: {
              url: values.url,
              ...(type === "youtube_download" &&
                urlValidationRefs.current.youtubethumbnail && {
                  image: { url: urlValidationRefs.current.youtubethumbnail },
                }),
            },
            source: type,
            asset_type: activeCustomUploadTab,
            meta_config: meta_config,
            fid: urlValidationRefs.current.gdriveMD5Checksum,
          },
          (response: any) => {
            const isSuccess = response?.status === 200;
            if (isSuccess) {
              if (response?.data?.preset_config?.meta) {
                localStorage.setItem(
                  "metaPresetId",
                  response.data.preset_config.meta
                );
              } else {
                const metaId = productList.find(
                  (item: any) => item?.id === singleCurrentUser?.team
                )?.presets?.meta;
                localStorage.setItem("metaPresetId", metaId);
              }
              if (response?.data?.preset_config?.social) {
                localStorage.setItem(
                  "socialPresetId",
                  response.data.preset_config.social
                );
              } else {
                const socialId = productList.find(
                  (item: any) => item?.id === singleCurrentUser?.team
                )?.presets?.social;
                localStorage.setItem("socialPresetId", socialId);
              }

              setTimeout(() => {
                getMedia({ id: response.data.id });
              }, 200);

              uploadingRefId.current = response?.data?.id;
              addFile({
                key: response.data.id,
                file: {
                  id: response.data.id,
                  name: response.data.title,
                  identifier: response.data.id,
                  progress: 0,
                  isUploadedSuccessfully: false,
                  type: response.data.type,
                  error: null,
                },
              });
              openUpload();
              closeModal();
            } else {
              toast.fail(response.error);
            }
          }
        );
      }
    },
  });

  // Initialize URL validation hook
  const urlValidation = useUrlValidation({
    formik,
    uploadType,
    selectedFile,
    onValidatingUrlChange,
  });

  // Destructure hook values for easier access
  const {
    isValidUrl,
    isValidPermission,
    isValidatingUrl,
    youtubetags,
    fileType,
    gdriveMD5Checksum,
    showRemove,
    channelWarning,
    handleUrlValidation,
    isValidatingUrlRef,
    valuesRef: urlValidationRefs,
  } = urlValidation;

  // Initialize preset management hook
  const presetManagement = usePresetManagement({
    formik,
    presetList,
    singleCurrentUser,
    currentPresetDetails,
    bunchProductList,
    getPresetList,
    adminPresetList,
    getCurrentPresetDetails,
    getAdminPresetList,
    getListProduct,
    adminCurrentPresetDetails,
    getAdminCurrentPresetDetails,
  });

  // Destructure preset management values
  const {
    presetProcessing,
    presetMeta,
    presetSocial,
    presetBranding,
    socialData,
    metaData,
    processingData,
    brandingData,
    previewType,
    setPreviewType,
  } = presetManagement;

  // Update fileType ref whenever it changes
  useEffect(() => {
    fileTypeRef.current = fileType;
    // Update validation schema when fileType changes
    if (formik) {
      const schema = Yup.object({
        url: Yup.string().required("URL is required"),
        title: Yup.string().required("Title is required"),
      });
      (formik as any).validationSchema = schema;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileType]);

  const handleFTPForm = () => {
    setShowFTPForm((prev) => !prev);
    urlValidation.setIsValidUrl(null);
    formik.setFieldValue("url", "");
    formik.setFieldTouched("url", false);
    formik.setFieldError("url", "");
  };

  // Helper function to render preset card
  const renderPresetCard = (
    presetType: "meta" | "processing" | "social" | "branding",
    formikField: "meta" | "processing" | "publishing" | "branding",
    label: string,
    data: any,
    teamKey: "product" | "team" = "team"
  ) => {
    const teamId = data?.[teamKey]?.[0];
    const needsStyle = presetType !== "meta";

    return (
      <SelectedMediaCard
        media={{
          label,
          value: data?.name,
          image: "",
          duration: "",
          thumb: false,
        }}
        type="pre"
        presetType={presetType}
        onClose={() => formik.setFieldValue(formikField, "")}
        showImage={false}
        style={needsStyle ? { width: "250px", height: "60px" } : undefined}
        onPreview={() => setPreviewType(presetType)}
        showPreviewButton={true}
        showProduct={true}
        team={bunchProductList?.find((item: any) => item?.id === teamId)?.name}
        isPreview={false}
      />
    );
  };

  const handleReset = () => {
    urlValidation.setShowRemove(false);
    setIsNextButtonClicked(false);
    nextButtonClickedRef.current = false;
    disableBlurValidationRef.current = false;
    formik.setFieldValue("url", "");
    formik.setFieldValue("title", "");
    formik.setFieldValue("desc", "");
    urlValidation.setIsValidUrl(null);
    setSelectedFile(null);
    urlValidation.setGdriveMD5Checksum(null);
    formik.setFieldTouched("url", false);
    formik.setFieldError("url", "");
    formik.validateForm().catch(() => {});
    urlValidation.setFileType(null);
    urlValidation.resetUrlValidation();
  };

  // Monitor form changes and notify parent component
  useEffect(() => {
    if (onFormChange) {
      onFormChange(formik.dirty);
    }
  }, [formik.dirty, onFormChange]);

  const handleDetailsSave = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      // Wait for any ongoing validation to complete

      if (isValidatingUrlRef.current) {
        let timeoutCount = 0;
        const maxTimeout = 50; // 5 seconds max wait
        while (isValidatingUrlRef.current && timeoutCount < maxTimeout) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          timeoutCount++;
        }
      }

      if (selectedFile === null && isValidUrl === false) {
        nextButtonClickedRef.current = false;
        return;
      }

      if (selectedFile === null && isValidPermission === false) {
        nextButtonClickedRef.current = false;
        return;
      }

      if (selectedFile === null) {
        if (!isValidUrl) {
          let validationResult = null;

          // Check if validation is already in progress
          if (isValidatingUrlRef.current) {
            // Wait for the existing validation to complete
            let timeoutCount = 0;
            const maxTimeout = 50; // 5 seconds max wait
            while (isValidatingUrlRef.current && timeoutCount < maxTimeout) {
              await new Promise((resolve) => setTimeout(resolve, 100));
              timeoutCount++;
            }
            // After waiting, check if URL is now valid
            if (!isValidUrl) {
              nextButtonClickedRef.current = false;
              return;
            }
          } else {
            validationResult = await handleUrlValidation();

            if (!validationResult || !validationResult.success) {
              nextButtonClickedRef.current = false;
              return;
            }

            // Refs are already updated synchronously in the hook, but wait a bit
            // to ensure React state updates are complete before proceeding
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        }
      }
      if (selectedFile === null) {
        const fetchedTitle = urlValidationRefs.current.fileName;
        if (!formik.values.title && fetchedTitle) {
          await formik.setFieldValue("title", fetchedTitle);
        }
        const fetchedDesc = urlValidationRefs.current.youtubedescription;
        if (!formik.values.desc && fetchedDesc) {
          await formik.setFieldValue("desc", fetchedDesc);
        }
      }

      Object.keys(formik.initialValues).forEach((field) => {
        formik.setFieldTouched(
          field as keyof typeof formik.initialValues,
          true
        );
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        nextButtonClickedRef.current = false;
        return;
      }

      try {
        await formik.submitForm();
        // Reset next button state after successful submission
        setIsNextButtonClicked(false);
        nextButtonClickedRef.current = false;
        disableBlurValidationRef.current = false;
      } catch (_error) {
        // Reset next button state even on error
        setIsNextButtonClicked(false);
        nextButtonClickedRef.current = false;
        disableBlurValidationRef.current = false;
      }
    },
    [
      isValidatingUrl,
      isValidatingUrlRef,
      selectedFile,
      isValidUrl,
      isValidPermission,
      formik,
      handleUrlValidation,
      isNextButtonClicked,
      nextButtonClickedRef,
      urlValidationRefs,
    ]
  );

  // Expose the handleDetailsSave function to parent
  useEffect(() => {
    if (onNextClick) {
      // Create a custom event listener for the next button
      const handleNextClick = async () => {
        // Disable blur validation immediately using ref
        disableBlurValidationRef.current = true;
        nextButtonClickedRef.current = true;
        setIsNextButtonClicked(true);
        await handleDetailsSave();
      };

      window.addEventListener("customNextClick", handleNextClick);
      return () => {
        window.removeEventListener("customNextClick", handleNextClick);
      };
    }
  }, [onNextClick, handleDetailsSave]);

  const onFileEvent = (event: string, data: any, cb?: any): any => {
    showUploadProgress();
    if (event === "fileAdded") {
      const file = data.file;
      file.isShorts = data.isShorts;
      file.type = "media";
      file.name = "media";
      file.asset_type = data.asset_type;
      formik.setFieldValue("url", file.fileName);
      const nameWithoutExt = file.fileName ? stripExtension(file.fileName) : "";
      formik.setFieldValue("title", nameWithoutExt);
      urlValidation.setShowRemove(true);
      UploadCallback.current = cb;
      getMediaDimensions(file.file ?? file).then(({ width, height }) => {
        const { aspect_ratio, orientation } = computeAspectRatioAndOrientation(
          width,
          height
        );
        file.aspect_ratio = aspect_ratio;
        file.orientation = orientation;
        if (orientation === 2) {
          file.asset_type = "shorts";
          file.isShorts = true;
        }
        setSelectedFile(file);
      });
    } else if (event === "fileProgress") {
      const file = data.file;
      const progress = Math.round(file.progress(false) * 1000) / 10;
      updateFileProgress({
        identifier: file.uniqueIdentifier,
        progress: progress,
      });
    } else if (event === "fileSuccess") {
      uploadingRef.current = false;
      setUploadedSuccessfully({
        identifier: data.id,
        flag: true,
      });
    } else if (event === "fileError") {
      uploadingRef.current = false;
      toast.fail(data.error);
    }
  };

  const sendCancelUploadRequest = () => {
    if (!uploadingRefId.current) return;
    const apiUrl = process.env.NX_APP_MEDIA_CANCEL ?? "";
    const formData = new FormData();
    formData.append("status", "-2");
    formData.append("id", uploadingRefId.current);
    updateMediaStatus({ id: uploadingRefId.current, status: -2 });
    const sent = navigator.sendBeacon(apiUrl, formData);
  };

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!uploadingRef.current) return;
    event.preventDefault();
    const message =
      "Your file upload is in progress. Are you sure you want to leave?";
    event.returnValue = message;
    window.addEventListener("unload", sendCancelUploadRequest);
    return message;
  };

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    urlValidation.setIsValidUrl(null);
    urlValidation.setIsValidPermission(true);
    setSelectedFile(null);
    urlValidation.setShowRemove(false);
    setIsNextButtonClicked(false);
    nextButtonClickedRef.current = false;
    disableBlurValidationRef.current = false;
    urlValidation.setGdriveMD5Checksum(null);
    formik.setFieldTouched("url", false, false);
    formik.setFieldError("url", "");
    formik.resetForm();
    urlValidation.setFileType(null);
    urlValidation.resetUrlValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadType]);

  return (
    <div className="px-4 mt-2">
      <div className="form-label">
        <div className="fw-medium text-primary">
          SOURCE FILE <span className="text-danger">*</span>
        </div>
        <small className="text-secondary d-block">
          <i>
            Supported platforms: YouTube, Facebook, Instagram, X, Google Drive,
            WeTransfer, FTP, and HTTPS for seamless uploads.
          </i>
        </small>
      </div>
      <div
        className="position-relative z-1 d-flex align-items-center justify-content-between mb-2"
        style={{ minHeight: "auto !important" }}
      >
        <div className="d-flex flex-grow-1 validation-label position-relative align-items-center">
          <div
            className="flex-grow-1"
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter") {
                handleUrlValidation();
              }
            }}
          >
            <TextInput
              name="url"
              placeholder=""
              onChange={(e) => {
                formik.handleChange(e);
                if (e.target.value !== formik.values.url) {
                  urlValidation.setIsValidUrl(null);
                  urlValidation.setIsValidPermission(true);
                }
              }}
              value={
                selectedFile !== null
                  ? formik.values.url.split(".")[0]
                  : formik.values.url
              }
              label=""
              showRequired={false}
              touched={formik.touched.url}
              error={formik.errors.url}
              onBlur={() => {
                if (isChangingTab || nextButtonClickedRef.current) {
                  return;
                }

                formik.setFieldTouched("url", true);
                formik.validateField("url");
                if (formik.values.url.length > 0 && !isValidatingUrl) {
                  setTimeout(() => {
                    // if (disableBlurValidationRef.current) {
                    //   return;
                    // }
                    handleUrlValidation();
                  }, 200);
                }
              }}
              disabled={selectedFile !== null || isValidUrl === true}
              endIcon={
                isValidatingUrl
                  ? "spinner-border spinner-border-sm text-primary"
                  : undefined
              }
              classNameLabel="position-absolute"
            />
          </div>
          {showRemove ? (
            <div className="mx-3">
              <Button type="outline-primary" onClick={handleReset}>
                Reset
              </Button>
            </div>
          ) : (
            <>
              <span className="mx-3">- OR -</span>
              <div className="d-flex align-items-center border">
                <Uploader
                  id={"video"}
                  type={uploadType}
                  onFileEvent={onFileEvent}
                  datatype={uploadType === "audio" ? "AUDIO" : "VIDEO"}
                >
                  <Button
                    type="outline-primary"
                    onClick={() => {
                      formik.setFieldTouched("url", false);
                      formik.setFieldError("url", "");
                      urlValidation.setIsValidUrl(null);
                    }}
                    className="border-0"
                  >
                    {" "}
                    Browse
                  </Button>
                </Uploader>
                <Button
                  type="outline-primary"
                  onClick={handleFTPForm}
                  className="border-0 border-start"
                >
                  <Ellipsis size={18} />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      {channelWarning && (
        <div className="mt-1 mb-2 text-warning small d-flex align-items-center gap-1">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>{channelWarning}</span>
        </div>
      )}
      {showFTPForm && <FTPAddForm getEncodedUrl={getEncodedUrl} />}
      {Object.keys(mediaFeatures).length > 0 && (
        <div
          className="position-relative row mt-3 pb-4"
          style={{ minHeight: "202px" }}
        >
          <div className="col-md-12 mb-3 pb-3 border-bottom">
            <div className="row m-0">
              <div className="form-label p-0 pb-2">
                <div className="fw-medium text-primary text-uppercase">
                  SELECT PRESETS
                </div>
                <small className="text-secondary d-block">
                  <i>
                    Cut down repetitive work. Apply Presets to your uploads.
                  </i>
                </small>
              </div>
              <div className="col-md-6 ps-0">
                {!formik.values.meta ? (
                  <CustomSelect
                    placeholder="Select Meta"
                    value={formik.values.meta || ""}
                    options={presetMeta.map((preset: any) => ({
                      label: preset.name,
                      value: preset.id,
                    }))}
                    handleChange={(newValue: any) => {
                      trackEvent("upload", {
                        category: "presets",
                        entity: "metadata",
                        action: "apply",
                        location: isWizard ? "wizard" : "non-wizard",
                        ...getTrackUserContext(),
                      });
                      formik.setFieldValue("meta", newValue.value);
                    }}
                  />
                ) : (
                  renderPresetCard(
                    "meta",
                    "meta",
                    "Select Meta",
                    metaData,
                    "product"
                  )
                )}
              </div>
              <div className="col-md-3 ps-0 H">
                {!formik.values.processing ? (
                  <CustomSelect
                    placeholder="Select Processing"
                    value={formik.values.processing || ""}
                    options={presetProcessing.map((preset: any) => ({
                      label: preset.name,
                      value: preset.id,
                    }))}
                    handleChange={(newValue: any) =>
                      formik.setFieldValue("processing", newValue.value)
                    }
                  />
                ) : (
                  renderPresetCard(
                    "processing",
                    "processing",
                    "Processing",
                    processingData,
                    "team"
                  )
                )}
              </div>
              <div className="col-md-6 ps-0">
                {!formik.values.publishing ? (
                  <CustomSelect
                    placeholder="Select Publishing"
                    value={formik.values.publishing || ""}
                    options={presetSocial.map((preset: any) => ({
                      label: preset.name,
                      value: preset.id,
                    }))}
                    handleChange={(newValue: any) => {
                      trackEvent("upload", {
                        category: "presets",
                        entity: "publishing",
                        action: "apply",
                        location: isWizard ? "wizard" : "non-wizard",
                        ...getTrackUserContext(),
                      });
                      formik.setFieldValue("publishing", newValue.value);
                    }}
                  />
                ) : (
                  renderPresetCard(
                    "social",
                    "publishing",
                    "Social",
                    socialData,
                    "team"
                  )
                )}
              </div>
              <div className="col-md-3 ps-0 H">
                {!formik.values.branding ? (
                  <CustomSelect
                    placeholder="Select Branding"
                    value={formik.values.branding || ""}
                    options={presetBranding.map((preset: any) => ({
                      label: preset.name,
                      value: preset.id,
                    }))}
                    handleChange={(newValue: any) =>
                      formik.setFieldValue("branding", newValue.value)
                    }
                  />
                ) : (
                  renderPresetCard(
                    "branding",
                    "branding",
                    "Branding",
                    brandingData,
                    "team"
                  )
                )}
              </div>
            </div>
          </div>
          <TextInput
            label="Title"
            className="col-md-12 mb-3"
            value={formik.values.title || ""}
            onChange={(e: any) => formik.setFieldValue("title", e.target.value)}
            onBlur={formik.handleBlur}
            placeholder="Enter Title"
            showRequired={true}
            error={formik.errors.title}
            touched={formik.touched.title}
            characterLimit={100}
          />
          <TextArea
            label="Description"
            className="col-md-12"
            value={formik.values.desc || ""}
            onChange={(e: any) => formik.setFieldValue("desc", e.target.value)}
            onBlur={formik.handleBlur}
            placeholder="Enter Description"
            name="desc"
            showRequired={false}
            characterLimit={5000}
          />
        </div>
      )}
      {previewType && (
        <Modal
          isOpen={!!previewType}
          onClose={() => setPreviewType(null)}
          width={
            previewType === "branding"
              ? "820px"
              : previewType === "processing"
                ? "820px"
                : "898px"
          }
          height={
            previewType === "meta"
              ? "100%"
              : previewType === "branding"
                ? "645px"
                : previewType === "processing"
                  ? "645px"
                  : "100%"
          }
          title={
            previewType === "social"
              ? "Social Preview"
              : previewType === "meta"
                ? "Meta Preview"
                : previewType === "processing"
                  ? "Processing Preview"
                  : "Branding Preview"
          }
          applyOverlay={false}
          footer={
            <Button
              onClick={(e) => {
                e.preventDefault();
                setPreviewType(null);
              }}
            >
              Close
            </Button>
          }
        >
          {previewType === "social" && (
            <SocialPublish
              isWizard={false}
              presetDetails={socialData.social}
              item={socialData}
              isPreview={true}
              isPreset={true}
              readOnlyMode={true}
            />
          )}
          {previewType === "meta" && (
            <CreateMetaPreset
              isPreview={true}
              selectedMedia={metaData}
              item={metaData}
              readOnlyMode={true}
            />
          )}
          {previewType === "processing" && (
            <ProcessingPreset
              isPreview={true}
              item={processingData}
              readOnlyMode={true}
            />
          )}
          {previewType === "branding" && (
            <BrandingPreset
              isPreview={true}
              presetData={brandingData}
              readOnlyMode={true}
              isPreset={true}
              isEdit={true}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  Language_Options: state.app.metaLanguage,
  bunchLanguageList: state.constants.bunchLanguageList.list,
  activeCustomUploadTab: state.media.activeCustomUploadTab,
  presetList: state.media.presetList.list,
  bunchProductList: state.constants.bunchProductList.list,
  singleCurrentUser: state.users.singleCurrentUser,
  productList: state.constants.productList.list,
  currentPresetDetails: state.media.currentPresetDetails.data,
  adminPresetList: state.media.adminPresetList.list,
  adminCurrentPresetDetails: state.media.adminCurrentPresetDetails.data,
});

const mapDispatch = {
  callRegisterMediaOnCms,
  callRegisterCustomMediaOnCms,
  showUploadProgress,
  updateFileProgress,
  addFile,
  setUploadedSuccessfully,
  setMediaDetails,
  emptyMediaDetails,
  emptyAssetDetails,
  getPresetList,
  getAdminPresetList,
  getMedia,
  getListProduct,
  getCurrentPresetDetails,
  getAdminCurrentPresetDetails,
};

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector> & IVideo;
export default connector(Video);
